import React, { useEffect, useState } from 'react';
import { Layout } from '../../components/layout';
import { Toast } from '../../components';
import { useToast } from '../../hooks/useToast';
import { settingsService } from '../../services';
import { InsurancePrices } from '../../types';

// Raw input values, kept as strings so the fields can be edited freely
type PriceInputs = Record<keyof InsurancePrices, string>;

const FIELDS: { key: keyof InsurancePrices; label: string }[] = [
  { key: 'car', label: 'Cars' },
  { key: 'truck', label: 'Trucks (category L)' },
];

const toInputs = (prices: InsurancePrices): PriceInputs => ({ car: String(prices.car), truck: String(prices.truck) });

// Returns null unless both inputs are numbers >= 0
const parseInputs = (inputs: PriceInputs): InsurancePrices | null => {
  const parse = (raw: string) => {
    const trimmed = raw.trim();
    const parsed = Number(trimmed);
    return trimmed === '' || Number.isNaN(parsed) || parsed < 0 ? null : parsed;
  };
  const car = parse(inputs.car);
  const truck = parse(inputs.truck);
  return car === null || truck === null ? null : { car, truck };
};

/**
 * System-wide insurance prices used by the "Add Insurance Fees" action on units.
 */
const InsurancePricesPage: React.FC = () => {
  const { toast, showToast, hideToast } = useToast();
  const [currentPrices, setCurrentPrices] = useState<InsurancePrices | null>(null);
  const [inputs, setInputs] = useState<PriceInputs>({ car: '', truck: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsService.getInsurancePrices()
      .then(prices => {
        setCurrentPrices(prices);
        setInputs(toInputs(prices));
      })
      .catch(err => {
        console.error('Error loading insurance prices:', err);
        showToast('Failed to load insurance prices', 'error');
      });
  }, [showToast]);

  const handleSave = async () => {
    const prices = parseInputs(inputs);
    if (!prices) {
      setError('Both prices must be numbers greater than or equal to 0');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const saved = await settingsService.setInsurancePrices(prices);
      setCurrentPrices(saved);
      setInputs(toInputs(saved));
      showToast('Insurance prices saved', 'success');
    } catch (err) {
      console.error('Error saving insurance prices:', err);
      showToast('Failed to save insurance prices', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Insurance Prices</h1>
          <p className="text-gray-600 dark:text-gray-300 text-sm">
            Used by "Add Insurance Fees" on units. Units that already have insurance keep their current amount.
          </p>
        </div>

        <div className="max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          {FIELDS.map(field => (
            <div key={field.key} className="space-y-2">
              <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                <span>{field.label}</span>
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                  Current price: {currentPrices ? currentPrices[field.key].toFixed(2) : 'Loading...'}
                </span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={inputs[field.key]}
                onChange={(event) => setInputs(prev => ({ ...prev, [field.key]: event.target.value }))}
                disabled={!currentPrices}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60"
              />
            </div>
          ))}

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={!currentPrices || saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Prices'}
            </button>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={hideToast}
        />
      )}
    </Layout>
  );
};

export default InsurancePricesPage;
